import { type PDFDocumentProxy, getDocument } from 'pdfjs-dist';
import * as pdfjs from 'pdfjs-dist';

// Polyfill Promise.withResolvers before any other code
if (typeof Promise.withResolvers === 'undefined') {
  const polyfilledResolvers = <T>() => {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
  if (typeof window !== 'undefined') {
    window.Promise.withResolvers = polyfilledResolvers;
  } else {
    global.Promise.withResolvers = polyfilledResolvers;
  }
}

export enum Strategy {
  Fast = 'fast',
  HiRes = 'hi_res',
  Auto = 'auto',
  OcrOnly = 'ocr_only',
}

if (typeof window !== 'undefined') {
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
    import.meta.url,
  ).toString();
}

interface PDFExtractorOptions {
  maxPages?: number;
}

async function extractPDFText(
  buffer: ArrayBuffer,
  options: PDFExtractorOptions = {},
): Promise<string> {
  const doc = await getDocument({
    data: new Uint8Array(buffer),
  }).promise;

  const pageCount =
    options.maxPages && options.maxPages > 0
      ? Math.min(options.maxPages, doc.numPages)
      : doc.numPages;

  const pageTexts = await Promise.all(
    Array.from({ length: pageCount }, (_, i) => extractPageText(doc, i + 1)),
  );

  await doc.destroy();
  return pageTexts.filter(Boolean).join('\n\n');
}

async function extractPageText(
  doc: PDFDocumentProxy,
  pageNum: number,
): Promise<string> {
  try {
    const page = await doc.getPage(pageNum);
    const textContent = await page.getTextContent();

    const pageText = textContent.items
      .map((item) => ('str' in item ? item.str : ''))
      .join(' ')
      .trim();
    return pageText;
  } catch {
    return '';
  }
}

export const getPdfStrategy = async (buffer: Buffer) => {
  //@ts-expect-error types are weird, maybe this should not be a buffer
  const results = await extractPDFText(buffer, { maxPages: 20 });

  // TDOO: investigate a more intelligent parser or method for finding if the pdf has text
  // most of the time pdfs will have text in the first 20 pages, but this is not really a reliable way to do it

  const subjectiveTextCheck = results.length > 1000;

  if (subjectiveTextCheck) return Strategy.Fast;
  return Strategy.Auto;
};
